import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";

const GroupManagers = ({ id }) => {
  const [managers, setManagers] = useState([]);
  const [initialAllowed, setInitialAllowed] = useState([]);
  const [selected, setSelected] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const setToast = useSetRecoilState(toastState);

  const themeColor = "#f57d36";
  const cardBg = useColorModeValue(
    "rgba(255, 255, 255, 0.6)",
    "rgba(26, 32, 44, 0.6)"
  );
  const borderColor = "rgba(245, 125, 54, 0.15)";

  const [, fetchManagers] = useAxios(
    { method: "GET", url: `/api/userprofiles/${id}/managers` },
    { manual: true }
  );
  const [, updateManagers] = useAxios(
    { method: "POST", url: `/api/userprofiles/${id}/managers` },
    { manual: true }
  );

  const loadManagers = async () => {
    try {
      setIsLoading(true);
      const res = await fetchManagers();
      const list = res?.data?.data || [];
      setManagers(list);
      const allowed = list.filter((m) => m.HasAccess).map((m) => m.Id);
      setInitialAllowed(allowed);
      setSelected(allowed);
    } catch (error) {
      logger.log(error);
      setToast({
        isOpen: true,
        title: "Error fetching managers",
        description: "Please try again",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadManagers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleManager = (managerId) => {
    setSelected((prev) =>
      prev.includes(managerId)
        ? prev.filter((mid) => mid !== managerId)
        : [...prev, managerId]
    );
  };

  const handleSave = async () => {
    const additions = selected.filter((mid) => !initialAllowed.includes(mid));
    const removals = initialAllowed.filter((mid) => !selected.includes(mid));
    if (additions.length === 0 && removals.length === 0) {
      setToast({
        isOpen: true,
        title: "No changes to save",
        status: "info",
        duration: 3000,
      });
      return;
    }
    try {
      setIsSaving(true);
      await updateManagers({ data: { additions, removals } });
      setInitialAllowed(selected);
      setToast({
        isOpen: true,
        title: "Manager access updated successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      setToast({
        isOpen: true,
        title: "Error updating access",
        description: error?.response?.data?.message || "Please try again",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="lg" color={themeColor} thickness="4px" />
      </Flex>
    );
  }

  return (
    <Box
      bg={cardBg}
      backdropFilter="blur(12px)"
      borderRadius="xl"
      border="1px solid"
      borderColor={borderColor}
      p={4}
    >
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="lg" fontWeight="bold">
          Manager Access
        </Text>
        <Button
          colorScheme="orange"
          size="sm"
          onClick={handleSave}
          isLoading={isSaving}
          isDisabled={managers.length === 0}
        >
          Save
        </Button>
      </Flex>
      <Text fontSize="sm" color="gray.500" mb={4}>
        Select which managers can access this user group.
      </Text>
      {managers.length === 0 ? (
        <Text color="gray.500">No managers found for this tenant.</Text>
      ) : (
        <Flex direction="column" gap={2}>
          {managers.map((manager) => (
            <Flex
              key={manager.Id}
              align="center"
              p={3}
              borderRadius="lg"
              border="1px solid"
              borderColor={
                selected.includes(manager.Id) ? themeColor : borderColor
              }
              bg={
                selected.includes(manager.Id)
                  ? "rgba(245, 125, 54, 0.08)"
                  : "transparent"
              }
            >
              <Checkbox
                colorScheme="orange"
                isChecked={selected.includes(manager.Id)}
                onChange={() => toggleManager(manager.Id)}
                sx={{
                  ".chakra-checkbox__control": {
                    borderColor: themeColor,
                    _checked: { bg: themeColor, borderColor: themeColor },
                  },
                }}
              />
              <Box ml={3}>
                <Text fontWeight="medium">
                  {`${manager.FirstName || ""} ${manager.LastName || ""}`.trim() ||
                    "Unnamed"}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {manager.Email}
                  {manager.MobileNumber ? ` • ${manager.MobileNumber}` : ""}
                </Text>
              </Box>
            </Flex>
          ))}
        </Flex>
      )}
    </Box>
  );
};

export default GroupManagers;
